import { ThemedText } from "@/components/themed-text";
import { Label } from "@/components/ui/label";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import Ionicons from "@react-native-vector-icons/ionicons";
import React, { useCallback, useState, type CSSProperties } from "react";
import { Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import { HelperText } from "react-native-paper";

export function calendarIndicatorImage(color: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><rect x='3' y='5' width='18' height='16' rx='2'/><path d='M8 3v4M16 3v4M3 11h18'/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

interface DateTimePickerInputProps {
  label: string;
  value?: Date;
  onChange: (date: Date) => void;
  error?: string;
  minimumDate?: Date;
}

export const DatePickerInput: React.FC<DateTimePickerInputProps> = ({
  label,
  value,
  onChange,
  error,
  minimumDate,
}) => {
  const { theme } = useAuth();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempDate, setTempDate] = useState<Date>(value || new Date());

  const openNativePicker = useCallback((input: HTMLInputElement | null) => {
    if (Platform.OS !== "web" || !input) {
      return;
    }
    input.focus();
    try {
      input.showPicker();
    } catch (error) {
      console.log("Picker not supported or blocked", error);
    }
  }, []);

  const handleOpenDatePicker = () => {
    setTempDate(value || new Date());
    onChange(value || new Date());
    setShowDatePicker(true);
  };

  return (
    <View style={styles.inputGroup}>
      <View style={styles.sectionHeader}>
        <Label label={label} />
      </View>

      {!showDatePicker && (
        <TouchableOpacity
          style={[
            styles.dateSelectionCard,
            { backgroundColor: theme.background, borderColor: theme.border },
            error && styles.inputError,
          ]}
          onPress={handleOpenDatePicker}
        >
          <Ionicons
            name="calendar-outline"
            size={20}
            color={BrandColors.primary}
          />
          <ThemedText
            style={[styles.dateValue, !value && styles.datePlaceholder]}
          >
            {value ? value.toLocaleDateString("en-GB") : "DD / MM / YYYY"}
          </ThemedText>
        </TouchableOpacity>
      )}

      {error && (
        <HelperText type="error" visible={!!error}>
          {error}
        </HelperText>
      )}

      {showDatePicker && (
        <>
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .date-picker-input {
                  border-style: solid;
                  border-color: ${theme.border};
                  outline: none;
                }
                .date-picker-input:focus {
                  border-color: ${theme.primary};
                  outline: 1.5px solid ${theme.primary};
                }
                .date-picker-input::-webkit-calendar-picker-indicator {
                  background-image: url("${calendarIndicatorImage(theme.text)}");
                  background-repeat: no-repeat;
                  background-position: center;
                  background-size: 18px 18px;
                  cursor: pointer;
                }
              `,
            }}
          />
          <input
            className="date-picker-input"
            type="date"
            value={tempDate?.toISOString().split("T")[0]}
            ref={openNativePicker}
            onChange={(e: any) => {
              if (e.target.value) {
                onChange(new Date(e.target.value));
              } else {
                onChange(new Date());
              }
              setShowDatePicker(false);
            }}
            min={minimumDate?.toISOString().split("T")[0]}
            style={
              StyleSheet.flatten([
                styles.dateInput,
                {
                  color: theme.text,
                  backgroundColor: theme.background,
                },
              ]) as CSSProperties
            }
          />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  inputGroup: {
    gap: 8,
  },
  datePickerActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 12,
  },
  datePickerButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  datePickerButtonCancel: {
    backgroundColor: "#f5f5f5",
  },
  datePickerButtonConfirm: {
    backgroundColor: BrandColors.primary,
  },
  datePickerButtonText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#666",
  },
  datePickerButtonTextConfirm: {
    color: "#fff",
  },
  dateSelectionCard: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    minHeight: 50,
    paddingHorizontal: 16,
    gap: 12,
  },
  dateValue: {
    flex: 1,
    fontSize: 16,
  },
  datePlaceholder: {
    color: BrandColors.mediumGray,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  inputError: {
    borderColor: BrandColors.error,
    borderWidth: 1,
  },
  dateInput: {
    fontSize: 16,
    borderWidth: 0.5,
    borderRadius: 8,
    padding: 12,
  },
});
